import { useCallback } from "react";
import useSWR from "swr";
import { productVariantsService } from "../../api_client";

/** The id pair a chat widget carries for each product it shows. */
export interface WidgetProductRef {
  /** Product template id — what the details/compare routes expect. */
  productId?: number;
  variantId?: number;
}

const isPositiveId = (id: unknown): id is number =>
  typeof id === "number" && Number.isInteger(id) && id > 0;

/**
 * Resolves the product template id for chat-widget items.
 *
 * Backend-built widgets carry both ids, but model-authored ones (e.g. the
 * reply to "View more products") and chats saved before `productId` existed
 * may carry only `variantId`. A variant id is NOT a template id — using it
 * as one opens an unrelated product or a 404 — so the missing template ids
 * are looked up from the variants. Items that can't be resolved yet (or at
 * all) resolve to null; callers render them without a link.
 */
export function useWidgetProductIds(refs: readonly WidgetProductRef[]) {
  const unresolvedVariantIds = [
    ...new Set(
      refs
        .filter((ref) => !isPositiveId(ref.productId) && isPositiveId(ref.variantId))
        .map((ref) => ref.variantId as number),
    ),
  ].sort((a, b) => a - b);

  const { data: templateIdByVariantId } = useSWR(
    unresolvedVariantIds.length > 0
      ? `widget-variant-template-ids-${unresolvedVariantIds.join(",")}`
      : null,
    async () => {
      // allSettled: one deleted/archived variant shouldn't block the rest.
      const results = await Promise.allSettled(
        unresolvedVariantIds.map((id) => productVariantsService.getProductVariantById(id)),
      );
      const map = new Map<number, number>();
      results.forEach((result, i) => {
        const templateId =
          result.status === "fulfilled" ? result.value.data.productTemplateId : null;
        if (isPositiveId(templateId)) map.set(unresolvedVariantIds[i], templateId);
      });
      return map;
    },
    { revalidateOnFocus: false, revalidateOnReconnect: false },
  );

  return useCallback(
    (ref: WidgetProductRef): number | null => {
      if (isPositiveId(ref.productId)) return ref.productId;
      if (!isPositiveId(ref.variantId)) return null;
      return templateIdByVariantId?.get(ref.variantId) ?? null;
    },
    [templateIdByVariantId],
  );
}
